import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { ArrowLeft, Pencil, Check, X, Trash2, Plus } from 'lucide-react';
import useBillingStore from '../../../store/billing';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { Skeleton } from '../../../components/ui/Skeleton';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { RelativeTime } from '../../../components/ui/RelativeTime';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  apiErrorMessage,
  apiErrorData,
  apiErrorStatus,
  apiBodyMessages,
} from '../../../utils/apiErrorMessage';
import StatusBadge from './components/StatusBadge';
import {
  formatCurrency,
  formatDate,
  isEditable,
  canApprove,
  canSendToBsale,
  canReject,
  DEDUCTION_CATEGORIES,
  TYPE_LABELS,
  STATUS_LABELS,
} from './utils/billingHelpers';

// Flujo de estados por tipo de documento (los pagos a repartidor no van a BSale).
const STEP_FLOW = {
  client_invoice: ['draft', 'reviewed', 'approved', 'sent_to_bsale', 'invoiced'],
  driver_payment: ['draft', 'reviewed', 'approved'],
};

// Stepper de estado para la cabecera del detalle. Sólo presentación sobre doc.status.
const StatusStepper = ({ status, type }) => {
  const { t } = useTranslation();
  const negative = status === 'rejected' || status === 'cancelled';
  const steps = STEP_FLOW[type] ?? STEP_FLOW.client_invoice;
  const currentIdx = steps.indexOf(status);

  return (
    <ol className="bill-stepper" aria-label={t("Estado del pre-documento")}>
      {steps.map((s, i) => {
        const state = negative
          ? 'muted'
          : i < currentIdx
            ? 'done'
            : i === currentIdx
              ? 'current'
              : 'todo';
        return (
          <li
            key={s}
            className={`bill-stepper__step bill-stepper__step--${state}`}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="bill-stepper__dot">{state === 'done' ? '✓' : i + 1}</span>
            <span className="bill-stepper__label">{t(STATUS_LABELS[s] ?? s)}</span>
          </li>
        );
      })}
      {negative && (
        <li className="bill-stepper__step bill-stepper__step--rejected" aria-current="step">
          <span className="bill-stepper__dot">!</span>
          <span className="bill-stepper__label">{t(STATUS_LABELS[status] ?? status)}</span>
        </li>
      )}
    </ol>
  );
};

// ── Sub-componentes ────────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, accent }) => (
  <div className={`bill-summary__card${accent ? ' bill-summary__card--accent' : ''}`}>
    <div className="bill-summary__label">{label}</div>
    <div className={`bill-summary__value${accent ? ' bill-summary__value--accent' : ''}`}>{value}</div>
  </div>
);

const InlineEdit = ({ value, onSave, disabled }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  const handleSave = () => {
    const num = parseFloat(val);
    if (!isNaN(num) && num >= 0) {
      onSave(num);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <span className="bill-inline">
        {formatCurrency(value)}
        {!disabled && (
          <button
            type="button"
            className="bill-inline__btn"
            onClick={() => { setVal(value); setEditing(true); }}
            title={t('Editar precio')}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="bill-inline">
      <Input
        type="number"
        size="sm"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="bill-inline__input"
        min={0}
        autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
      />
      <button type="button" className="bill-inline__btn bill-inline__btn--ok" onClick={handleSave} title={t('Guardar')}>
        <Check className="w-4 h-4" />
      </button>
      <button type="button" className="bill-inline__btn bill-inline__btn--cancel" onClick={() => setEditing(false)} title={t('Cancelar')}>
        <X className="w-4 h-4" />
      </button>
    </span>
  );
};

const LINE_COLS = '40px 1.4fr 2fr 1fr 1.5fr 1fr';
const LINE_PAGE = 50; // Se muestran las entregas por bloques para no montar cientos de nodos.
const DED_COLS = '1.2fr 2fr 1.2fr 1fr auto';

// El backend exige ObjectId en `packageId` (AddDeductionItemDto.@IsMongoId), pero el
// operador ve el N° de seguimiento del paquete (ej: BT-001234): se valida antes de enviar.
const MONGO_ID_RE = /^[a-f\d]{24}$/i;

// Estado inicial del formulario de deducción (y al que se vuelve al cerrarlo).
const EMPTY_DEDUCTION_FORM = {
  category: 'paquete_roto',
  description: '',
  packageId: '',
  amount: '',
};

// `assertEditable` (pre-document.service) protege updateLineItem, addDeduction y
// removeDeduction con el MISMO 400: el doc ya se aprobó/envió/rechazó en otra pestaña y
// la copia en memoria quedó vieja, así que la vista sigue ofreciendo lápiz y papelera.
const NOT_EDITABLE_RE = /^No se puede editar un pre-documento en estado/;

const isNotEditableError = (err) =>
  apiErrorStatus(err) === 400 && NOT_EDITABLE_RE.test(apiErrorMessage(err, '') ?? '');

// 400 de validación del DTO: array de mensajes con la ruta del campo como prefijo.
const hasPackageIdError = (err) =>
  apiBodyMessages(apiErrorData(err)).some((message) => message.startsWith('packageId'));

// ── Vista principal ────────────────────────────────────────────────────────────

const PreDocumentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    selectedPreDocument: doc,
    loadingDetail,
    saving,
    fetchPreDocument,
    clearSelectedPreDocument,
    updateLineItem,
    addDeduction,
    removeDeduction,
    approve,
    reject,
    sendToBsale,
  } = useBillingStore();

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deductionModal, setDeductionModal] = useState(false);
  const [deductionForm, setDeductionForm] = useState(EMPTY_DEDUCTION_FORM);
  const [confirmBsaleModal, setConfirmBsaleModal] = useState(false);
  const [visibleLines, setVisibleLines] = useState(LINE_PAGE);

  useEffect(() => {
    fetchPreDocument(id).catch((err) => {
      toast.error(apiErrorMessage(err, t('No se pudo cargar el pre-documento')));
      navigate('/admin/billing/pre-documents');
    });
    return () => clearSelectedPreDocument();
  }, [id]);

  // El documento cambió de estado en otra pestaña: se recarga para que la vista deje de
  // ofrecer edición. El error ya se mostró con el toast, así que el fallo de ESTA recarga
  // se descarta a propósito (no hay nada nuevo que decirle al operador).
  const refreshIfNotEditable = useCallback(
    (err) => {
      if (!isNotEditableError(err)) return;
      fetchPreDocument(id).catch(() => {});
    },
    [id, fetchPreDocument],
  );

  const handleLineItemEdit = useCallback(
    async (idx, netUnitValue) => {
      try {
        await updateLineItem(id, idx, netUnitValue);
        toast.success(t('Precio actualizado'));
      } catch (err) {
        toast.error(apiErrorMessage(err, t('Error al actualizar el precio')));
        refreshIfNotEditable(err);
      }
    },
    [id, refreshIfNotEditable],
  );

  const handleAddDeduction = async () => {
    const amount = parseFloat(deductionForm.amount);
    if (!deductionForm.description.trim() || isNaN(amount) || amount >= 0) {
      toast.error(t('Completa todos los campos. El monto debe ser negativo.'));
      return;
    }
    // Vacío sigue permitido (la deducción puede no ir atada a un paquete); con valor, el
    // backend solo acepta el ObjectId.
    const packageId = deductionForm.packageId.trim();
    if (packageId && !MONGO_ID_RE.test(packageId)) {
      toast.error(t('Ingresa el ID interno del paquete (24 caracteres).'));
      return;
    }
    try {
      await addDeduction(id, {
        category: deductionForm.category,
        description: deductionForm.description,
        packageId: packageId || undefined,
        amount,
      });
      toast.success(t('Deducción agregada'));
      setDeductionModal(false);
      setDeductionForm(EMPTY_DEDUCTION_FORM);
    } catch (err) {
      if (hasPackageIdError(err)) {
        toast.error(t('El ID del paquete no es válido.'));
      } else {
        toast.error(apiErrorMessage(err, t('Error al agregar la deducción')));
        if (isNotEditableError(err)) {
          // Con el modal abierto, la recarga (loadingDetail) lo desmonta un instante y al
          // volver reaparecería con el formulario lleno sobre un doc ya aprobado. Se cierra
          // y se limpia ANTES de recargar.
          setDeductionModal(false);
          setDeductionForm(EMPTY_DEDUCTION_FORM);
        }
        refreshIfNotEditable(err);
      }
    }
  };

  const handleRemoveDeduction = async (idx) => {
    try {
      await removeDeduction(id, idx);
      toast.success(t('Deducción eliminada'));
    } catch (err) {
      toast.error(apiErrorMessage(err, t('Error al eliminar la deducción')));
      refreshIfNotEditable(err);
    }
  };

  const handleApprove = async () => {
    try {
      await approve(id);
      toast.success(t('Pre-documento aprobado'));
    } catch (err) {
      toast.error(apiErrorMessage(err, t('Error al aprobar')));
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) {
      toast.error(t('Ingresa un motivo de rechazo (mínimo 5 caracteres)'));
      return;
    }
    try {
      await reject(id, rejectReason);
      toast.success(t('Pre-documento rechazado'));
      setRejectModal(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('Error al rechazar')));
    }
  };

  const handleSendToBsale = async () => {
    try {
      await sendToBsale(id);
      toast.success(t('Pre-documento enviado a BSale'));
      setConfirmBsaleModal(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('Error al enviar a BSale')));
    }
  };

  if (loadingDetail || !doc) {
    // Skeleton acorde al layout del detalle: cabecera + 5 cards de resumen + tabla.
    return (
      <div className="bill-page" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        {/* Cabecera */}
        <div className="bill-card bill-card--pad">
          <div className="d-flex justify-content-between" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            <div className="d-grid" style={{ gap: '0.5rem' }}>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <Skeleton className="h-8 w-24 rounded-md" />
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          </div>
        </div>
        {/* Resumen (5 cards) */}
        <div className="bill-summary">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="bill-summary__card" key={i}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-24" style={{ marginTop: '0.5rem' }} />
            </div>
          ))}
        </div>
        {/* Tabla de entregas */}
        <div className="bill-card bill-card--clip" style={{ marginTop: '1rem' }}>
          <TableSkeleton rows={8} columns={6} className="border-0" />
        </div>
      </div>
    );
  }

  const editable = isEditable(doc.status);
  const lineItems = doc.lineItems ?? [];
  const shownLines = lineItems.slice(0, visibleLines);
  const deductions = doc.deductionItems ?? [];

  return (
    <div className="bill-page">
      {/* Volver */}
      <button type="button" className="bill-back" onClick={() => navigate('/admin/billing/pre-documents')}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a la lista')}
      </button>

      {/* Cabecera */}
      <div className="bill-card bill-card--pad">
        <div className="bill-doc-head">
          <div>
            <div className="bill-doc-head__title">
              <h2>{t(TYPE_LABELS[doc.type] ?? doc.type)}</h2>
              <StatusBadge status={doc.status} />
            </div>
            <div className="bill-doc-head__meta">
              {t('Periodo')}: <strong>{formatDate(doc.periodStart)}</strong> → <strong>{formatDate(doc.periodEnd)}</strong>
            </div>
            {doc.notes && <div className="bill-doc-head__note">{t('Nota')}: {doc.notes}</div>}
          </div>

          <div className="bill-doc-head__actions">
            {canReject(doc.status) && (
              <Button size="sm" variant="outline" onClick={() => setRejectModal(true)} disabled={saving}>
                {t('Rechazar')}
              </Button>
            )}
            {canApprove(doc.status) && (
              <Button size="sm" variant="primary" onClick={handleApprove} loading={saving}>
                {t('Aprobar')}
              </Button>
            )}
            {canSendToBsale(doc.status) && doc.type === 'client_invoice' && (
              <Button size="sm" variant="primary" onClick={() => setConfirmBsaleModal(true)} loading={saving}>
                {t('Enviar a BSale')}
              </Button>
            )}
            {doc.bsaleDocumentPdfUrl && (
              <Button size="sm" variant="outline" onClick={() => window.open(doc.bsaleDocumentPdfUrl, '_blank')}>
                {t('Ver PDF BSale')}
              </Button>
            )}
          </div>
        </div>

        {/* Stepper de estado */}
        <StatusStepper status={doc.status} type={doc.type} />
      </div>

      {/* Resumen */}
      <div className="bill-summary">
        <SummaryCard label={t('Entregas')} value={lineItems.length} />
        <SummaryCard label={t('Subtotal')} value={formatCurrency(doc.subtotal)} />
        <SummaryCard label={t('Deducciones')} value={formatCurrency(doc.totalDeductions)} />
        <SummaryCard label={t('IVA 19%')} value={formatCurrency(doc.taxAmount)} />
        <SummaryCard label={t('Total')} value={formatCurrency(doc.totalAmount)} accent />
      </div>

      {/* Detalle de entregas */}
      <div className="bill-card bill-card--clip">
        <div className="bill-card__head">
          <h3 className="bill-card__title">
            {t('Detalle de entregas')} <span>({lineItems.length})</span>
          </h3>
          {editable && <span className="bill-hint bill-hint--accent">{t('Haz clic en el lápiz para editar el precio')}</span>}
        </div>

        {isMobile ? (
          <div className="bill-cards">
            {shownLines.map((item, i) => (
              <div key={i} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title bill-td-track">{item.followingNumber}</span>
                  <span className="bill-td-amount">{formatCurrency(item.totalAmount)}</span>
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Descripción')}</span>
                    <span className="bill-card-item__v bill-td-muted">{item.description}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Entregado')}</span>
                    <span className="bill-card-item__v bill-td-sub">{item.deliveredAt ? formatDate(item.deliveredAt) : '-'}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Precio Unitario')}</span>
                    <span className="bill-card-item__v">
                      <InlineEdit value={item.netUnitValue} onSave={(v) => handleLineItemEdit(i, v)} disabled={!editable} />
                      {item.netUnitValue !== item.originalUnitValue && (
                        <div className="bill-inline__orig">{t('Original')}: {formatCurrency(item.originalUnitValue)}</div>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': LINE_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">#</span>
              <span className="bill-grid__hcell" role="columnheader">{t('N° Seguimiento')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Descripción')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Entregado')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Precio Unitario')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Total')}</span>
            </div>
            {shownLines.map((item, i) => (
              <div key={i} className="bill-grid__row" role="row">
                <span className="bill-td-idx" role="cell">{i + 1}</span>
                <span className="bill-td-track" role="cell">{item.followingNumber}</span>
                <span className="bill-td-muted" role="cell">{item.description}</span>
                <span className="bill-td-sub" role="cell">{item.deliveredAt ? formatDate(item.deliveredAt) : '-'}</span>
                <span role="cell">
                  <InlineEdit value={item.netUnitValue} onSave={(v) => handleLineItemEdit(i, v)} disabled={!editable} />
                  {item.netUnitValue !== item.originalUnitValue && (
                    <div className="bill-inline__orig">{t('Original')}: {formatCurrency(item.originalUnitValue)}</div>
                  )}
                </span>
                <span className="bill-td-amount" role="cell">{formatCurrency(item.totalAmount)}</span>
              </div>
            ))}
          </div>
        )}

        {visibleLines < lineItems.length && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVisibleLines((v) => v + LINE_PAGE)}
            >
              {t('Ver más')} ({lineItems.length - visibleLines})
            </Button>
          </div>
        )}
      </div>

      {/* Deducciones */}
      <div className="bill-card bill-card--clip">
        <div className="bill-card__head">
          <h3 className="bill-card__title">
            {t('Deducciones')} <span>({deductions.length})</span>
          </h3>
          {editable && (
            <Button size="sm" variant="outline" onClick={() => setDeductionModal(true)} iconLeft={<Plus className="w-4 h-4" />}>
              {t('Agregar deducción')}
            </Button>
          )}
        </div>

        {deductions.length === 0 ? (
          <div className="bill-empty" style={{ padding: '1.5rem' }}>{t('No hay deducciones')}</div>
        ) : isMobile ? (
          <div className="bill-cards">
            {deductions.map((d, i) => (
              <div key={i} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="bill-pill bill-pill--warning">
                    {t(DEDUCTION_CATEGORIES.find((c) => c.value === d.category)?.label ?? d.category)}
                  </span>
                  <span className="bill-td-neg">{formatCurrency(d.amount)}</span>
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Descripción')}</span>
                    <span className="bill-card-item__v bill-td-muted">{d.description}</span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Paquete')}</span>
                    <span className="bill-card-item__v bill-td-mono">{d.packageId ?? '-'}</span>
                  </div>
                </div>
                {editable && (
                  <div className="bill-card-item__foot">
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveDeduction(i)} iconLeft={<Trash2 className="w-4 h-4" />}>
                      {t('Eliminar')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': DED_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Categoría')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Descripción')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Paquete')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Monto')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader" aria-label={t('Acciones')} />
            </div>
            {deductions.map((d, i) => (
              <div key={i} className="bill-grid__row" role="row">
                <span role="cell">
                  <span className="bill-pill bill-pill--warning">
                    {t(DEDUCTION_CATEGORIES.find((c) => c.value === d.category)?.label ?? d.category)}
                  </span>
                </span>
                <span className="bill-td-muted" role="cell">{d.description}</span>
                <span className="bill-td-mono" role="cell">{d.packageId ?? '-'}</span>
                <span className="bill-td-neg" role="cell">{formatCurrency(d.amount)}</span>
                <span className="bill-grid__cell--actions" role="cell">
                  {editable && (
                    <button
                      type="button"
                      className="bill-inline__btn bill-inline__btn--cancel"
                      onClick={() => handleRemoveDeduction(i)}
                      title={t('Eliminar deducción')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historial de cambios */}
      {doc.auditLog && doc.auditLog.length > 0 && (
        <details className="bill-audit">
          <summary className="bill-audit__summary">{t('Historial de cambios')} ({doc.auditLog.length})</summary>
          <div className="bill-audit__body">
            {[...doc.auditLog].reverse().map((entry, i) => (
              <div key={i} className="bill-audit__row">
                {entry.timestamp ? <RelativeTime className="bill-audit__time" value={entry.timestamp} t={t} /> : '-'}
                <span className="bill-audit__action">
                  <strong>{entry.action}</strong>
                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                    <span> — {JSON.stringify(entry.changes)}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Info BSale */}
      {doc.bsaleDocumentId && (
        <div className="bill-alert bill-alert--success">
          <span>
            <strong>{t('Enviado a BSale')}</strong> — {t('Documento N°')} {doc.bsaleDocumentId}
            {doc.bsaleDocumentUrl && (
              <> | <a href={doc.bsaleDocumentUrl} target="_blank" rel="noreferrer">{t('Ver en BSale')}</a></>
            )}
          </span>
        </div>
      )}

      {/* Modal Rechazar */}
      <Modal
        open={rejectModal}
        onClose={() => setRejectModal(false)}
        size="sm"
        title={t('Rechazar pre-documento')}
        footer={
          <>
            <Button variant="outline" onClick={() => setRejectModal(false)}>{t('Cancelar')}</Button>
            <Button variant="danger" onClick={handleReject} loading={saving}>{t('Confirmar rechazo')}</Button>
          </>
        }
      >
        <div className="bill-field">
          <label className="bill-label">{t('Motivo del rechazo')}</label>
          <textarea
            className="bill-textarea"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('Describe el motivo del rechazo...')}
          />
        </div>
      </Modal>

      {/* Modal Deducción */}
      <Modal
        open={deductionModal}
        onClose={() => setDeductionModal(false)}
        size="md"
        title={t('Agregar deducción')}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeductionModal(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleAddDeduction} loading={saving}>{t('Agregar deducción')}</Button>
          </>
        }
      >
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('Categoría')}</label>
          <Select
            value={deductionForm.category}
            onChange={(e) => setDeductionForm((f) => ({ ...f, category: e.target.value }))}
          >
            {DEDUCTION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{t(c.label)}</option>
            ))}
          </Select>
        </div>
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('Descripción')}</label>
          <Input
            value={deductionForm.description}
            onChange={(e) => setDeductionForm((f) => ({ ...f, description: e.target.value }))}
            placeholder={t('Ej: Paquete BT-001234 roto al momento de entrega')}
          />
        </div>
        <div className="bill-field" style={{ marginBottom: '1rem' }}>
          <label className="bill-label">{t('ID del paquete (opcional)')}</label>
          <Input
            value={deductionForm.packageId}
            onChange={(e) => setDeductionForm((f) => ({ ...f, packageId: e.target.value }))}
            placeholder={t('ObjectId del paquete')}
          />
        </div>
        <div className="bill-field">
          <label className="bill-label">{t('Monto a descontar (valor negativo, ej: -15000)')}</label>
          <Input
            type="number"
            value={deductionForm.amount}
            onChange={(e) => setDeductionForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="-15000"
          />
        </div>
      </Modal>

      {/* Modal Confirmar BSale */}
      <Modal
        open={confirmBsaleModal}
        onClose={() => setConfirmBsaleModal(false)}
        size="sm"
        title={t('Enviar a BSale')}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmBsaleModal(false)}>{t('Cancelar')}</Button>
            <Button onClick={handleSendToBsale} loading={saving}>{t('Confirmar envío')}</Button>
          </>
        }
      >
        <p className="text-fg" style={{ margin: 0 }}>
          {t('Se creará una Nota de Venta en BSale por un total de')}{' '}
          <strong>{formatCurrency(doc.totalAmount)}</strong>.
        </p>
        <p className="bill-hint" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          {t('Esta acción marcará los paquetes como facturados y no podrá revertirse.')}
        </p>
      </Modal>
    </div>
  );
};

export default PreDocumentDetail;
